import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, CalendarBlank, HandCoins, SealCheck, WhatsappLogo, CalendarCheck, Building } from "@phosphor-icons/react";
import { PROPERTIES, WHATSAPP_URL, CALENDAR_URL } from "@/data/content";
import { useLanguage } from "@/i18n";
import Footer from "./Footer";

const EASE = [0.33, 1, 0.68, 1];

export default function ProjectDetail() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const p = PROPERTIES.find((x) => x.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [id]);

  useEffect(() => {
    if (p) document.title = `${p.name} | Charbel Moubarak — Dubai Off-Plan`;
    return () => {
      document.title = "Charbel Moubarak | Luxury Real Estate Specialist — Dubai & International";
    };
  }, [p]);

  if (!p) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-charcoal px-6 text-center">
        <p className="font-serif text-3xl text-gold">{t.project.notFound}</p>
        <Link
          to="/"
          data-testid="project-back-link"
          className="mt-6 border border-gold px-6 py-3 text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-charcoal"
        >
          {t.project.back}
        </Link>
      </div>
    );
  }

  const waHref = `${WHATSAPP_URL}?text=${encodeURIComponent(`Hello Charbel, I'm interested in ${p.name}.`)}`;

  const META = [
    { icon: Building, label: t.project.type, value: p.type[lang], testid: "project-meta-type" },
    { icon: CalendarBlank, label: t.properties.handover, value: p.completion[lang], testid: "project-meta-handover" },
    { icon: HandCoins, label: t.properties.payment, value: p.payment[lang], testid: "project-meta-payment" },
  ];

  return (
    <div className="min-h-screen bg-charcoal" data-testid="project-detail-page">
      <header className="sticky top-0 z-50 border-b border-gold/15 bg-charcoal/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
          <Link
            to="/"
            data-testid="project-back-link"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft size={16} className="rtl:rotate-180" /> {t.project.back}
          </Link>
          <span className="font-serif text-base uppercase tracking-[0.3em] text-[#FAFAFA]">
            {t.brand.first} <span className="text-gold">{t.brand.last}</span>
          </span>
        </div>
      </header>

      <section className="relative h-[46vh] overflow-hidden md:h-[56vh]">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: EASE }}
          src={p.image}
          alt={p.name}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/30 to-transparent" />
        <span className="absolute left-6 top-6 border border-gold/40 bg-charcoal/80 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm md:left-10 rtl:left-auto rtl:right-6 rtl:md:right-10">
          {t.properties.filters[p.dev]}
        </span>
        <span className="absolute right-6 top-6 bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-charcoal md:right-10 rtl:left-6 rtl:right-auto rtl:md:left-10">
          {t.properties.offplan}
        </span>
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-6xl px-6 pb-8 md:px-10">
          <h1 data-testid="project-title" className="font-serif text-3xl uppercase tracking-wide text-[#FAFAFA] md:text-5xl">
            {p.name}
          </h1>
          <p className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] md:text-sm">
            <MapPin size={16} className="shrink-0 text-gold" /> {p.location[lang]}
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-12 px-6 py-14 md:px-10 lg:grid-cols-[1.4fr_1fr]">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}>
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80">{t.project.highlights}</p>
          <p className="mt-5 font-serif text-xl leading-relaxed text-[#E5C07B] md:text-2xl">{p.about[lang]}</p>
          <p className="mt-6 leading-relaxed text-[#A3A3A3]">{p.features[lang]}</p>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {META.map((m) => (
              <div key={m.testid} data-testid={m.testid} className="border border-gold/15 bg-surface p-5">
                <m.icon size={22} weight="duotone" className="text-gold" />
                <p className="mt-3 text-[9px] uppercase tracking-[0.25em] text-[#A3A3A3]/70">{m.label}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-[#FAFAFA]">{m.value}</p>
              </div>
            ))}
          </div>
          {p.visa && (
            <span data-testid="project-visa-chip" className="mt-6 inline-flex items-center gap-1.5 border border-gold/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-gold">
              <SealCheck size={14} weight="duotone" /> {t.properties.visa}
            </span>
          )}
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="h-fit border border-gold/20 bg-surface p-8 lg:sticky lg:top-24"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">{t.project.from}</p>
          <p data-testid="project-price" className="mt-2 font-serif text-3xl text-gold-gradient">{p.price[lang]}</p>
          <a
            data-testid="project-whatsapp-button"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 flex items-center justify-center gap-3 bg-gold px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-gold-bright"
          >
            <WhatsappLogo size={18} weight="duotone" /> {t.project.enquire}
          </a>
          <a
            data-testid="project-meeting-button"
            href={CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-3 border border-gold/50 px-6 py-4 text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold/10"
          >
            <CalendarCheck size={18} weight="duotone" /> {t.project.book}
          </a>
          <p className="mt-6 border-l-2 border-gold/40 pl-4 text-xs italic leading-relaxed text-[#A3A3A3] rtl:border-l-0 rtl:border-r-2 rtl:pl-0 rtl:pr-4">
            {t.contact.promise}
          </p>
        </motion.aside>
      </section>

      <Footer />
    </div>
  );
}
